import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { showToast } from '@/components/base/Toast';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/api/client';

type Role = 'Nông hộ' | 'Hợp tác xã';
type Step = 'select_role' | 'fill_form' | 'htx_fill_form' | 'success';

const fieldClass = (hasError: boolean) => `w-full min-h-[56px] rounded-2xl border bg-slate-50 px-5 text-base text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-agrida-500 focus:bg-white focus:ring-2 focus:ring-agrida-500/20 ${hasError ? 'border-red-400 bg-red-50' : 'border-slate-200'}`;

export default function RegisterPage() {
  const [step, setStep] = useState<Step>('select_role');
  const [registeredRole, setRegisteredRole] = useState<Role>('Nông hộ');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [htxName, setHtxName] = useState('');
  const [htxCode, setHtxCode] = useState('');
  const [htxAddress, setHtxAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { registerFarmer, registerHtx, login } = useAuth();
  const navigate = useNavigate();

  const clearError = (field: string) => setErrors((current) => ({ ...current, [field]: '' }));

  const selectRole = (selectedRole: Role) => {
    setStep(selectedRole === 'Nông hộ' ? 'fill_form' : 'htx_fill_form');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};

    if (!name.trim()) nextErrors.name = 'Vui lòng nhập họ tên';
    if (!phone.trim()) nextErrors.phone = 'Vui lòng nhập số điện thoại';
    else if (!/^\d{10}$/.test(phone)) nextErrors.phone = 'Số điện thoại phải có đúng 10 chữ số';
    if (!password) nextErrors.password = 'Vui lòng nhập mật khẩu';
    else if (password.length < 8) nextErrors.password = 'Mật khẩu cần tối thiểu 8 ký tự';
    if (!confirmPassword) nextErrors.confirmPassword = 'Vui lòng xác nhận mật khẩu';
    else if (password !== confirmPassword) nextErrors.confirmPassword = 'Mật khẩu xác nhận không khớp';

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      showToast('Vui lòng kiểm tra lại thông tin', 'warning');
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      await registerFarmer({ phone: phone.trim(), password, fullName: name.trim() });
      await login(phone.trim(), password);
      showToast('Đăng ký thành công!', 'success');
      setRegisteredRole('Nông hộ');
      setStep('success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setErrors({ phone: 'Số điện thoại này đã được đăng ký' });
        showToast('Số điện thoại này đã được đăng ký', 'error');
      } else if (err instanceof Error) {
        showToast(err.message, 'error');
      } else {
        showToast('Đăng ký thất bại, vui lòng thử lại', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleHtxSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};

    if (!htxName.trim()) nextErrors.htxName = 'Vui lòng nhập tên hợp tác xã';
    if (!htxCode.trim()) nextErrors.htxCode = 'Vui lòng nhập mã hợp tác xã';
    if (!name.trim()) nextErrors.name = 'Vui lòng nhập họ tên người đại diện';
    if (!phone.trim()) nextErrors.phone = 'Vui lòng nhập số điện thoại';
    else if (!/^\d{10}$/.test(phone)) nextErrors.phone = 'Số điện thoại phải có đúng 10 chữ số';
    if (!password) nextErrors.password = 'Vui lòng nhập mật khẩu';
    else if (password.length < 8) nextErrors.password = 'Mật khẩu cần tối thiểu 8 ký tự';
    if (!confirmPassword) nextErrors.confirmPassword = 'Vui lòng xác nhận mật khẩu';
    else if (password !== confirmPassword) nextErrors.confirmPassword = 'Mật khẩu xác nhận không khớp';

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      showToast('Vui lòng kiểm tra lại thông tin', 'warning');
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      await registerHtx({
        name: htxName.trim(),
        code: htxCode.trim(),
        address: htxAddress.trim() || undefined,
        phone: phone.trim(),
        password,
        fullName: name.trim(),
      });
      await login(phone.trim(), password);
      showToast('Đăng ký hợp tác xã thành công!', 'success');
      setRegisteredRole('Hợp tác xã');
      setStep('success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        showToast(err.message, 'error');
      } else if (err instanceof Error) {
        showToast(err.message, 'error');
      } else {
        showToast('Đăng ký thất bại, vui lòng thử lại', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (step === 'success') {
    if (registeredRole === 'Hợp tác xã') {
      return (
        <main className="mx-auto flex min-h-[100dvh] max-w-[540px] flex-col bg-white px-5 py-6 sm:px-8">
          <section className="flex flex-1 flex-col justify-center">
            <div className="mb-7 text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-agrida-50">
                <i className="ri-check-line text-4xl text-agrida-600" aria-hidden="true" />
              </div>
              <h1 className="text-xl font-extrabold text-slate-900">Đăng ký hợp tác xã thành công!</h1>
              <p className="mt-1 text-sm text-slate-500">Hợp tác xã và tài khoản quản trị của bạn đã sẵn sàng sử dụng.</p>
            </div>
            <button type="button" onClick={() => navigate('/htx/tong-quan')} className="mt-5 min-h-[48px] w-full rounded-xl bg-agrida-600 px-4 font-semibold text-white transition-colors hover:bg-agrida-700">
              Vào trang tổng quan HTX
            </button>
          </section>
        </main>
      );
    }
    return (
      <main className="mx-auto flex min-h-[100dvh] max-w-[540px] flex-col bg-white px-5 py-6 sm:px-8">
        <section className="flex flex-1 flex-col justify-center">
          <div className="mb-7 text-center">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-agrida-50">
              <i className="ri-check-line text-4xl text-agrida-600" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-extrabold text-slate-900">Đăng ký thành công!</h1>
            <p className="mt-1 text-sm text-slate-500">Tài khoản của bạn đã được tạo.</p>
          </div>
          <div className="rounded-2xl border border-agrida-100 bg-agrida-50 p-5">
            <h2 className="font-bold text-slate-900">Hoàn thiện hồ sơ nông hộ</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">Tạo hồ sơ nông hộ (chọn HTX, CCCD) để sử dụng đầy đủ các chức năng của Agrida.</p>
            <button type="button" onClick={() => navigate('/nong-ho/tao-ho-so')} className="mt-5 min-h-[48px] w-full rounded-xl bg-agrida-600 px-4 font-semibold text-white transition-colors hover:bg-agrida-700">
              Tạo hồ sơ ngay
            </button>
          </div>
          <button type="button" onClick={() => navigate('/nong-ho/tong-quan')} className="mt-3 min-h-[48px] w-full rounded-xl border border-slate-200 px-4 font-semibold text-slate-600 transition-colors hover:bg-slate-50">
            Bỏ qua, thực hiện sau
          </button>
        </section>
      </main>
    );
  }

  if (step === 'htx_fill_form') {
    return (
      <main className="mx-auto min-h-[100dvh] max-w-[540px] bg-white">
        <header className="grid h-[72px] grid-cols-[48px_1fr_48px] items-center border-y border-slate-100 px-5">
          <button type="button" onClick={() => setStep('select_role')} className="flex h-12 w-12 items-center justify-center rounded-xl text-slate-700 transition-colors hover:bg-slate-50" aria-label="Quay lại chọn loại tài khoản">
            <i className="ri-arrow-left-line text-2xl" aria-hidden="true" />
          </button>
          <h1 className="text-center text-xl font-extrabold text-slate-950">Đăng ký Hợp tác xã</h1>
        </header>

        <form onSubmit={handleHtxSubmit} className="px-5 pb-8 pt-7 sm:px-8">
          <fieldset>
            <legend className="mb-4 text-base font-medium uppercase tracking-wide text-slate-500">Thông tin hợp tác xã</legend>
            <div className="space-y-4">
              <div>
                <label htmlFor="htx-name" className="mb-2 block text-lg font-medium text-slate-800">Tên hợp tác xã <span className="text-red-500">*</span></label>
                <input id="htx-name" value={htxName} onChange={(event) => { setHtxName(event.target.value); clearError('htxName'); }} className={fieldClass(Boolean(errors.htxName))} placeholder="VD: HTX Nông nghiệp Hòa Phát" aria-invalid={Boolean(errors.htxName)} />
                {errors.htxName && <p className="mt-1.5 text-sm text-red-600" role="alert">{errors.htxName}</p>}
              </div>
              <div>
                <label htmlFor="htx-code" className="mb-2 block text-lg font-medium text-slate-800">Mã hợp tác xã <span className="text-red-500">*</span></label>
                <input id="htx-code" value={htxCode} onChange={(event) => { setHtxCode(event.target.value); clearError('htxCode'); }} className={fieldClass(Boolean(errors.htxCode))} placeholder="VD: HTX-HP-2026" aria-invalid={Boolean(errors.htxCode)} />
                {errors.htxCode && <p className="mt-1.5 text-sm text-red-600" role="alert">{errors.htxCode}</p>}
              </div>
              <div>
                <label htmlFor="htx-address" className="mb-2 block text-lg font-medium text-slate-800">Địa chỉ</label>
                <input id="htx-address" value={htxAddress} onChange={(event) => setHtxAddress(event.target.value)} className={fieldClass(false)} placeholder="Địa chỉ trụ sở (không bắt buộc)" />
              </div>
            </div>
          </fieldset>

          <fieldset className="mt-5 border-t border-slate-100 pt-5">
            <legend className="mb-4 text-base font-medium uppercase tracking-wide text-slate-500">Người đại diện</legend>
            <div>
              <label htmlFor="htx-rep-name" className="mb-2 block text-lg font-medium text-slate-800">Họ tên <span className="text-red-500">*</span></label>
              <input id="htx-rep-name" autoComplete="name" value={name} onChange={(event) => { setName(event.target.value); clearError('name'); }} className={fieldClass(Boolean(errors.name))} placeholder="Nhập họ và tên" aria-invalid={Boolean(errors.name)} />
              {errors.name && <p className="mt-1.5 text-sm text-red-600" role="alert">{errors.name}</p>}
            </div>
          </fieldset>

          <fieldset className="mt-5 border-t border-slate-100 pt-5">
            <legend className="mb-4 text-base font-medium uppercase tracking-wide text-slate-500">Thông tin đăng nhập</legend>
            <div className="space-y-4">
              <div>
                <label htmlFor="htx-phone" className="mb-2 block text-lg font-medium text-slate-800">Số điện thoại <span className="text-red-500">*</span></label>
                <input id="htx-phone" type="tel" inputMode="numeric" autoComplete="tel" maxLength={10} value={phone} onChange={(event) => { setPhone(event.target.value.replace(/\D/g, '').slice(0, 10)); clearError('phone'); }} className={fieldClass(Boolean(errors.phone))} placeholder="Số điện thoại dùng để đăng nhập" aria-invalid={Boolean(errors.phone)} />
                {errors.phone && <p className="mt-1.5 text-sm text-red-600" role="alert">{errors.phone}</p>}
              </div>
              <PasswordField id="htx-password" label="Mật khẩu" value={password} onChange={(value) => { setPassword(value); clearError('password'); }} show={showPassword} onToggle={() => setShowPassword((current) => !current)} error={errors.password} placeholder="Tối thiểu 8 ký tự" autoComplete="new-password" />
              <PasswordField id="htx-confirm-password" label="Xác nhận mật khẩu" value={confirmPassword} onChange={(value) => { setConfirmPassword(value); clearError('confirmPassword'); }} show={showConfirmPassword} onToggle={() => setShowConfirmPassword((current) => !current)} error={errors.confirmPassword} placeholder="Nhập lại mật khẩu" autoComplete="new-password" />
            </div>
          </fieldset>

          <button type="submit" disabled={loading} className="mt-5 min-h-[60px] w-full rounded-2xl bg-agrida-600 px-5 text-lg font-bold text-white transition-colors hover:bg-agrida-700 disabled:cursor-not-allowed disabled:opacity-60">
            {loading ? 'Đang tạo hợp tác xã...' : 'Tạo hợp tác xã'}
          </button>
        </form>
      </main>
    );
  }

  if (step === 'select_role') {
    return (
      <main className="mx-auto flex min-h-[100dvh] max-w-[540px] flex-col bg-white px-5 py-6 sm:px-8">
        <section className="flex flex-1 flex-col justify-center">
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-extrabold text-slate-900">Tạo tài khoản Agrida</h1>
            <p className="mt-2 text-sm text-slate-500">Chọn loại tài khoản phù hợp với bạn</p>
          </div>
          <div className="space-y-3">
            <button type="button" onClick={() => selectRole('Nông hộ')} className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-5 text-left transition-colors hover:border-orange-300 hover:bg-orange-50/40">
              <i className="ri-user-line text-2xl text-orange-600" aria-hidden="true" />
              <span><strong className="block text-slate-900">Nông hộ</strong><small className="mt-1 block text-slate-500">Dành cho hộ nông dân canh tác</small></span>
            </button>
            <button type="button" onClick={() => selectRole('Hợp tác xã')} className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-5 text-left transition-colors hover:border-agrida-300 hover:bg-agrida-50/40">
              <i className="ri-building-line text-2xl text-agrida-600" aria-hidden="true" />
              <span><strong className="block text-slate-900">Hợp tác xã</strong><small className="mt-1 block text-slate-500">Dành cho hợp tác xã quản lý thành viên</small></span>
            </button>
          </div>
          <p className="mt-6 text-center text-sm text-slate-500">Đã có tài khoản? <Link to="/dang-nhap" className="font-semibold text-agrida-600 hover:text-agrida-700">Đăng nhập</Link></p>
        </section>
      </main>
    );
  }

  return (
    <main className="mx-auto min-h-[100dvh] max-w-[540px] bg-white">
      <header className="grid h-[72px] grid-cols-[48px_1fr_48px] items-center border-y border-slate-100 px-5">
        <button type="button" onClick={() => setStep('select_role')} className="flex h-12 w-12 items-center justify-center rounded-xl text-slate-700 transition-colors hover:bg-slate-50" aria-label="Quay lại chọn loại tài khoản">
          <i className="ri-arrow-left-line text-2xl" aria-hidden="true" />
        </button>
        <h1 className="text-center text-xl font-extrabold text-slate-950">Đăng ký Nông hộ</h1>
      </header>

      <form onSubmit={handleSubmit} className="px-5 pb-8 pt-7 sm:px-8">
        <div>
          <label htmlFor="register-name" className="mb-2 block text-lg font-medium text-slate-800">Họ tên <span className="text-red-500">*</span></label>
          <input id="register-name" autoComplete="name" value={name} onChange={(event) => { setName(event.target.value); clearError('name'); }} className={fieldClass(Boolean(errors.name))} placeholder="Nhập họ và tên" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'register-name-error' : undefined} />
          {errors.name && <p id="register-name-error" className="mt-1.5 text-sm text-red-600" role="alert">{errors.name}</p>}
        </div>

        <fieldset className="mt-5 border-t border-slate-100 pt-5">
          <legend className="mb-4 text-base font-medium uppercase tracking-wide text-slate-500">Thông tin đăng nhập</legend>
          <div className="space-y-4">
            <div>
              <label htmlFor="register-phone" className="mb-2 block text-lg font-medium text-slate-800">Số điện thoại <span className="text-red-500">*</span></label>
              <input id="register-phone" type="tel" inputMode="numeric" autoComplete="tel" maxLength={10} value={phone} onChange={(event) => { setPhone(event.target.value.replace(/\D/g, '').slice(0, 10)); clearError('phone'); }} className={fieldClass(Boolean(errors.phone))} placeholder="Số điện thoại dùng để đăng nhập" aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'register-phone-error' : undefined} />
              {errors.phone && <p id="register-phone-error" className="mt-1.5 text-sm text-red-600" role="alert">{errors.phone}</p>}
            </div>
            <PasswordField id="register-password" label="Mật khẩu" value={password} onChange={(value) => { setPassword(value); clearError('password'); }} show={showPassword} onToggle={() => setShowPassword((current) => !current)} error={errors.password} placeholder="Tối thiểu 8 ký tự" autoComplete="new-password" />
            <PasswordField id="register-confirm-password" label="Xác nhận mật khẩu" value={confirmPassword} onChange={(value) => { setConfirmPassword(value); clearError('confirmPassword'); }} show={showConfirmPassword} onToggle={() => setShowConfirmPassword((current) => !current)} error={errors.confirmPassword} placeholder="Nhập lại mật khẩu" autoComplete="new-password" />
          </div>
        </fieldset>

        <button type="submit" disabled={loading} className="mt-5 min-h-[60px] w-full rounded-2xl bg-agrida-600 px-5 text-lg font-bold text-white transition-colors hover:bg-agrida-700 disabled:cursor-not-allowed disabled:opacity-60">
          {loading ? 'Đang tạo tài khoản...' : 'Tạo tài khoản'}
        </button>
      </form>
    </main>
  );
}

function PasswordField({ id, label, value, onChange, show, onToggle, error, placeholder, autoComplete }: { id: string; label: string; value: string; onChange: (value: string) => void; show: boolean; onToggle: () => void; error?: string; placeholder: string; autoComplete: string }) {
  const errorId = `${id}-error`;
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-lg font-medium text-slate-800">{label} <span className="text-red-500">*</span></label>
      <div className="relative">
        <input id={id} type={show ? 'text' : 'password'} autoComplete={autoComplete} value={value} onChange={(event) => onChange(event.target.value)} className={`${fieldClass(Boolean(error))} pr-14`} placeholder={placeholder} aria-invalid={Boolean(error)} aria-describedby={error ? errorId : undefined} />
        <button type="button" onClick={onToggle} className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" aria-label={show ? `Ẩn ${label.toLowerCase()}` : `Hiện ${label.toLowerCase()}`}>
          <i className={show ? 'ri-eye-off-line text-xl' : 'ri-eye-line text-xl'} aria-hidden="true" />
        </button>
      </div>
      {error && <p id={errorId} className="mt-1.5 text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
}
